import { Candle, SessionLevels } from '../../types';

export function calcVWAP(candles: Candle[]): number[] {
  if (!candles.length) return [];
  let cumTV = 0, cumV = 0;
  return candles.map(c => {
    const tp = (c.high + c.low + c.close) / 3;
    const v = c.volume || 1;
    cumTV += tp * v;
    cumV += v;
    return cumTV / cumV;
  });
}

// Anchored VWAP starting from given index
export function calcAnchoredVWAP(candles: Candle[], anchorIdx: number): number[] {
  const result = new Array(candles.length).fill(NaN);
  let cumTV = 0, cumV = 0;
  for (let i = anchorIdx; i < candles.length; i++) {
    const c = candles[i];
    const tp = (c.high + c.low + c.close) / 3;
    const v = c.volume || 1;
    cumTV += tp * v;
    cumV += v;
    result[i] = cumTV / cumV;
  }
  return result;
}

export function calcSessionLevels(candles: Candle[]): SessionLevels {
  if (!candles.length) return { orHigh: 0, orLow: 0, sessionHigh: 0, sessionLow: 0, open: 0 };
  const n = Math.min(6, candles.length);
  const first = candles.slice(0, n);
  return {
    orHigh: Math.max(...first.map(c => c.high)),
    orLow: Math.min(...first.map(c => c.low)),
    sessionHigh: Math.max(...candles.map(c => c.high)),
    sessionLow: Math.min(...candles.map(c => c.low)),
    open: candles[0].open,
  };
}

export function calcRelVol(candles: Candle[], window = 20): number {
  if (candles.length < 2) return 1;
  const recent = candles[candles.length - 1].volume;
  const baseline = candles.slice(-Math.min(window + 1, candles.length), -1);
  if (!baseline.length) return 1;
  const avg = baseline.reduce((s, c) => s + c.volume, 0) / baseline.length;
  return avg > 0 ? recent / avg : 1;
}

export function calcATR(candles: Candle[], period = 14): number {
  if (candles.length < 2) return 0;
  const trs: number[] = [];
  for (let i = 1; i < candles.length; i++) {
    const c = candles[i], p = candles[i - 1];
    trs.push(Math.max(c.high - c.low, Math.abs(c.high - p.close), Math.abs(c.low - p.close)));
  }
  const slice = trs.slice(-period);
  return slice.reduce((s, v) => s + v, 0) / slice.length;
}

export function detectSwingHighs(candles: Candle[], lookback = 3): Array<{ price: number; idx: number; ts: number }> {
  const highs: Array<{ price: number; idx: number; ts: number }> = [];
  for (let i = lookback; i < candles.length - lookback; i++) {
    const p = candles[i].high;
    if (candles.slice(i - lookback, i).every(c => c.high <= p) &&
        candles.slice(i + 1, i + lookback + 1).every(c => c.high <= p)) {
      highs.push({ price: p, idx: i, ts: candles[i].ts });
    }
  }
  return highs;
}

export function detectSwingLows(candles: Candle[], lookback = 3): Array<{ price: number; idx: number; ts: number }> {
  const lows: Array<{ price: number; idx: number; ts: number }> = [];
  for (let i = lookback; i < candles.length - lookback; i++) {
    const p = candles[i].low;
    if (candles.slice(i - lookback, i).every(c => c.low >= p) &&
        candles.slice(i + 1, i + lookback + 1).every(c => c.low >= p)) {
      lows.push({ price: p, idx: i, ts: candles[i].ts });
    }
  }
  return lows;
}

export function findEqualLevels(
  levels: Array<{ price: number; idx: number; ts: number }>,
  tolerancePct = 0.003
): Array<{ price: number; count: number }> {
  const clusters: Array<{ price: number; count: number }> = [];
  const used = new Set<number>();
  for (let i = 0; i < levels.length; i++) {
    if (used.has(i)) continue;
    const cluster = [levels[i]];
    for (let j = i + 1; j < levels.length; j++) {
      if (!used.has(j) && Math.abs(levels[i].price - levels[j].price) / levels[i].price < tolerancePct) {
        cluster.push(levels[j]);
        used.add(j);
      }
    }
    if (cluster.length >= 2) {
      clusters.push({ price: cluster.reduce((s, c) => s + c.price, 0) / cluster.length, count: cluster.length });
    }
    used.add(i);
  }
  return clusters;
}

export function nearestRoundNumbers(price: number): number[] {
  const mags = [1, 5, 10, 25, 50, 100, 250, 500, 1000];
  const mag = mags.find(m => price / m < 20) || 10;
  const lower = Math.floor(price / mag) * mag;
  const upper = Math.ceil(price / mag) * mag;
  return [lower, upper].filter(v => Math.abs(v - price) / price > 0.001);
}

export function detectGapEdges(candles: Candle[], meta: { prevHigh?: number; prevLow?: number; prevClose?: number }): number[] {
  const gaps: number[] = [];
  if (!candles.length) return gaps;
  const sessionOpen = candles[0].open;
  if (meta.prevClose && Math.abs(sessionOpen - meta.prevClose) / meta.prevClose > 0.002) {
    gaps.push(meta.prevClose); // gap edge = prior close
  }
  return gaps;
}

export function findNearestCandle(candles: Candle[], ts: number): number {
  let best = 0, bestDist = Infinity;
  for (let i = 0; i < candles.length; i++) {
    const d = Math.abs(candles[i].ts - ts);
    if (d < bestDist) { bestDist = d; best = i; }
  }
  return best;
}
